import React, { useState, useEffect, useMemo } from "react";
import { useToolCallAccordion } from "@/hooks/useToolCallAccordion";
import {
  WrenchIcon,
  SearchIcon,
  FileTextIcon,
  DatabaseIcon,
  CloudIcon,
  TerminalSquareIcon,
  EyeIcon,
  FrameIcon,
  ChevronRightIcon,
  BotIcon,
  Globe,
  SparklesIcon,
  TicketIcon,
  HelpCircle,
} from "lucide-react";
import { ApplyFileEditsBlock } from "./ApplyFileEditsBlock";
import { ClaudeCodeEditToolBlock } from "./ClaudeCodeEditToolBlock";
import { WebSearchResultsBlock } from "./WebSearchResultsBlock";
import { getRandomStatusText } from "./utils";
import {
  AskUserQuestionsBlock,
  ASK_USER_QUESTIONS_TOOL_NAME,
} from "./AskUserQuestionsBlock";
import { useToolResults } from "../contexts/ToolResultsContext";
import { getIssueToolBlock, isIssueOrPMAgentTool } from "./IssueToolBlocks";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { usePrevious } from "@/utils/utils";
import { useDatabaseSyncWrapperStore } from "@/components/contexts/databaseSyncStore";
import _ from "lodash";
import {
  TOOL_RENDERERS,
  TOOL_HEADINGS,
  DefaultToolRenderer,
} from "./toolRenderers";

const getAccordionTriggerClasses = () =>
  cn(
    "flex items-center text-xs font-medium text-secondary-n-400 px-0 py-1.5",
    "hover:no-underline",
    "[&[data-state=closed]>svg:last-child]:hidden [&[data-state=open]>svg:last-child]:hidden",
  );

const getChevronClasses = (accordionValue: string) =>
  cn(
    "w-4 h-4 text-secondary-n-400 transition-all absolute inset-0",
    accordionValue === "tool-content"
      ? "rotate-90 opacity-100"
      : "opacity-0 group-hover/tool:opacity-100",
  );

const ISSUE_TOOLS = ["createIssueTool", "updateIssueTool", "deleteIssueTool"];

interface UserAnswer {
  questionId: string;
  answer: string | string[];
}

interface ToolCallBlockProps {
  toolName: string;
  args: any;
  status: "in-progress" | "succeeded" | "failed" | "aborted";
  toolResult?: any;
  toolCallId?: string;
  messageId: string;
  isLastMessage?: boolean;
  isChatLoading?: boolean;
  onAnswerQuestions?: (
    toolCallId: string,
    answers: UserAnswer[],
    skipped: boolean,
  ) => void;
  isSubmittingQuestions?: boolean;
  pendingQuestionsToolCallId?: string;
}

const AnimatedStatusText = ({ baseText }: { baseText: string }) => {
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev >= 3 ? 1 : prev + 1));
    }, 200);

    return () => clearInterval(interval);
  }, []);

  return (
    <span className="text-xs text-secondary-n-500">
      {baseText}
      {".".repeat(dotCount)}
    </span>
  );
};

export const ToolCallBlock: React.FC<ToolCallBlockProps> = ({
  toolName,
  args,
  status,
  toolResult,
  toolCallId,
  messageId,
  isLastMessage = false,
  isChatLoading = false,
  onAnswerQuestions,
  isSubmittingQuestions = false,
  pendingQuestionsToolCallId,
}) => {
  const normalizedToolName = toolName;
  const { accordionValue, setAccordionValue } = useToolCallAccordion({
    status,
    isLastMessage,
    isChatLoading,
  });

  const prevStatus = usePrevious(status);
  const {
    getResult: getToolResultFromContext,
    setSubmittedAnswers,
    getSubmittedAnswers,
  } = useToolResults();

  useEffect(() => {
    if (prevStatus === "in-progress" && status === "succeeded") {
      if (normalizedToolName === "createStoryBoardTool") {
        useDatabaseSyncWrapperStore.getState().triggerRefetchCanvas();
      } else if (ISSUE_TOOLS.includes(normalizedToolName)) {
        useDatabaseSyncWrapperStore.getState().triggerRefetchIssues();
      }
    }
  }, [prevStatus, status, normalizedToolName]);

  const isFailedOrAborted = status === "failed" || status === "aborted";

  const toolIcon = useMemo(() => {
    const getToolSpecificIcon = () => {
      switch (normalizedToolName) {
        case "readFilesTool":
        case "readFilePartialTool":
          return EyeIcon;
        case "globTool":
        case "grepTool":
          return SearchIcon;
        case "supabaseDeployEdgeFunctionTool":
          return CloudIcon;
        case "supabaseRunMigrationTool":
          return DatabaseIcon;
        case "runTerminalCommandTool":
        case "restartDevServerTool":
          return TerminalSquareIcon;
        case "createStoryBoardTool":
          return FrameIcon;
        case "createOrUpdateStoriesTool":
          return FileTextIcon;
        case "taskTool":
          return BotIcon;
        case "todoWriteTool":
          return FileTextIcon;
        case "webSearchTool":
        case "web_search":
          return Globe;
        case "web_fetch":
          return Globe;
        case "Skill":
          return SparklesIcon;
        case "createIssueTool":
        case "updateIssueTool":
        case "deleteIssueTool":
        case "listIssuesTool":
        case "readIssueTool":
          return TicketIcon;
        case "exploreCodebaseTool":
          return SearchIcon;
        case "askUserQuestionsTool":
          return HelpCircle;
        default:
          return WrenchIcon;
      }
    };

    const IconComponent = getToolSpecificIcon();

    return (
      <IconComponent
        className={`w-4 h-4 transition-all ${
          isFailedOrAborted ? "text-red-500" : "text-secondary-n-400"
        }`}
      />
    );
  }, [normalizedToolName, isFailedOrAborted]);

  // Special tool blocks that have their own full rendering
  if (normalizedToolName === "applyFileEditsTool") {
    return (
      <ApplyFileEditsBlock
        args={args}
        status={status}
        toolResult={toolResult}
        toolCallId={toolCallId}
        messageId={messageId}
      />
    );
  }

  if (normalizedToolName === "editTool") {
    return (
      <ClaudeCodeEditToolBlock
        args={args}
        status={status}
        toolResult={toolResult}
        toolCallId={toolCallId}
        messageId={messageId}
        isLastMessage={isLastMessage}
        isChatLoading={isChatLoading}
      />
    );
  }

  if (normalizedToolName === ASK_USER_QUESTIONS_TOOL_NAME) {
    const canSubmit = !!toolCallId && pendingQuestionsToolCallId === toolCallId;
    const effectiveToolResult =
      toolResult ||
      (toolCallId ? getToolResultFromContext(toolCallId) : undefined);

    const pendingAnswers = toolCallId
      ? getSubmittedAnswers(toolCallId)
      : undefined;
    let submittedAnswers: UserAnswer[] | undefined = pendingAnswers?.answers;
    let wasSkipped = pendingAnswers?.skipped || false;

    // Prefer backend-confirmed answers over pending answers
    if (effectiveToolResult) {
      try {
        const result =
          typeof effectiveToolResult === "string"
            ? JSON.parse(effectiveToolResult)
            : effectiveToolResult;
        const content = result?.content || result;
        if (content?.answers?.length) {
          submittedAnswers = content.answers;
        }
        if (content?.skipped === true) {
          wasSkipped = true;
        }
      } catch {
        // Ignore parse errors
      }
    }

    return (
      <AskUserQuestionsBlock
        context={args?.context || ""}
        questions={args?.questions || []}
        status={status}
        onSubmit={(answers, skipped) => {
          if (toolCallId) {
            setSubmittedAnswers(toolCallId, { answers, skipped });
            onAnswerQuestions?.(toolCallId, answers, skipped);
          }
        }}
        isSubmitting={isSubmittingQuestions}
        canSubmit={canSubmit}
        submittedAnswers={submittedAnswers}
        wasSkipped={wasSkipped}
      />
    );
  }

  const getToolHeading = () => {
    if (TOOL_HEADINGS[toolName]) {
      return TOOL_HEADINGS[toolName];
    }
    return _.startCase(toolName.replace(/_/g, " ").trim());
  };

  const stringifyRender = (value: any) => {
    if (typeof value !== "string") {
      try {
        value = JSON.stringify(value);
      } catch (e) {
        console.error("Failed to stringify value:", e, value);
        return value?.toString() || "";
      }
    }
    return value;
  };

  const getFallbackText = () => {
    if (status === "failed") {
      const errorMessage = toolResult?.message || "Tool execution failed";
      return <span className="text-xs text-red-400">{errorMessage}</span>;
    }
    if (status === "aborted") {
      return (
        <span className="text-xs text-red-400">Tool execution aborted</span>
      );
    }
    if (status === "in-progress") {
      return <AnimatedStatusText baseText={getRandomStatusText()} />;
    }
    return (
      <span className="text-xs text-secondary-n-500">
        {getRandomStatusText()}
      </span>
    );
  };

  const renderWithErrorIfFailed = (argumentsContent: React.ReactNode) => {
    if (status === "failed" || status === "aborted") {
      const errorMessage =
        toolResult?.message ||
        (status === "aborted"
          ? "Tool execution aborted"
          : "Tool execution failed");

      return (
        <div className="space-y-1">
          {argumentsContent}
          <div className="text-xs text-red-400">{errorMessage}</div>
        </div>
      );
    }

    return argumentsContent;
  };

  const getWebSearchResults = () => {
    if (
      (normalizedToolName !== "web_search" &&
        normalizedToolName !== "webSearchTool") ||
      status !== "succeeded" ||
      !toolResult
    ) {
      return null;
    }

    try {
      const result =
        typeof toolResult === "string" ? JSON.parse(toolResult) : toolResult;

      let results: any[] = [];

      if (result?.output?.value && Array.isArray(result.output.value)) {
        results = result.output.value;
      } else if (result?.value && Array.isArray(result.value)) {
        results = result.value;
      } else if (Array.isArray(result)) {
        results = result;
      }

      if (results.length > 0) {
        return <WebSearchResultsBlock results={results} />;
      }
    } catch (e) {
      console.error("Failed to parse web search results:", e);
    }

    return null;
  };

  const getToolContent = () => {
    // Handle issue/PM agent tools with dedicated components
    if (isIssueOrPMAgentTool(normalizedToolName)) {
      const IssueToolBlock = getIssueToolBlock(normalizedToolName);
      if (IssueToolBlock) {
        return (
          <IssueToolBlock
            args={args}
            status={status}
            toolResult={toolResult}
            renderWithErrorIfFailed={renderWithErrorIfFailed}
            AnimatedStatusText={AnimatedStatusText}
          />
        );
      }
    }

    // Use registry for standard tools
    const ToolRenderer = TOOL_RENDERERS[normalizedToolName];
    if (ToolRenderer) {
      return (
        <ToolRenderer
          args={args}
          status={status}
          toolResult={toolResult}
          renderWithErrorIfFailed={renderWithErrorIfFailed}
          getFallbackText={getFallbackText}
          stringifyRender={stringifyRender}
        />
      );
    }

    // Default renderer for unknown tools
    return (
      <DefaultToolRenderer
        args={args}
        status={status}
        toolResult={toolResult}
        renderWithErrorIfFailed={renderWithErrorIfFailed}
        getFallbackText={getFallbackText}
        stringifyRender={stringifyRender}
      />
    );
  };

  return (
    <Accordion
      type="single"
      value={accordionValue}
      onValueChange={setAccordionValue}
      collapsible
      className="group/tool"
    >
      <AccordionItem value="tool-content" className="border-none">
        <AccordionTrigger className={getAccordionTriggerClasses()}>
          <div className="flex items-center gap-2">
            <div className="relative w-4 h-4">
              <ChevronRightIcon className={getChevronClasses(accordionValue)} />
              <div
                className={cn(
                  "absolute inset-0 transition-opacity",
                  accordionValue === "tool-content"
                    ? "opacity-0"
                    : "opacity-100 group-hover/tool:opacity-0",
                )}
              >
                {toolIcon}
              </div>
            </div>
            <span className="font-semibold text-xs">{getToolHeading()}</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="transition-all">
          <div className="relative ml-6">
            <div className="absolute left-[-18px] top-0 bottom-0 w-px bg-secondary-n-700"></div>
            <div className="space-y-3">
              {getToolContent()}
              {getWebSearchResults()}
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
